import { Alert, Avatar, Box, CircularProgress, useTheme } from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { useCurrentOrgId } from "@/providers/auth/hooks";
import {
  CommentRecord,
  CreateCommentRequest,
  CommentEntityType,
  UserSuggestion,
} from "@/shared/domain/comments";
import {
  useCreateComment,
  useUpdateComment,
} from "@/shared/domain/comments/hooks";
import { useOrgMembers } from "@/shared/domain/orgs/hooks";

import { CommentInput } from "./CommentInput";

interface CommentFormProps {
  entityId: string;
  entityType: CommentEntityType;
  commentToEdit?: CommentRecord;
  enableTimestamps?: boolean;
  onSubmit?: (comment: CommentRecord) => void;
  onCancel?: () => void;
}

export function CommentForm({
  enableTimestamps = false,
  entityId,
  entityType,
  onSubmit,
  onCancel,
  commentToEdit,
}: CommentFormProps) {
  const theme = useTheme();

  const { currentUser } = useAuth();
  const { data: orgMembers } = useOrgMembers(useCurrentOrgId());

  const {
    mutate: createComment,
    isPending: isCreating,
    error: createError,
  } = useCreateComment();

  const {
    mutate: updateComment,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateComment();

  const [userSuggestionList, setUserSuggestionList] = React.useState<
    UserSuggestion[]
  >([]);

  React.useEffect(() => {
    if (!orgMembers) {
      return;
    }

    const validRoles = orgMembers.filter((role) => {
      return !role.user.is_service_user;
    });

    setUserSuggestionList(
      validRoles.map((role) => {
        const suggestion = {
          id: role.user.user_id,
          display: role.user?.name ?? role.user.user_id,
        } as UserSuggestion;

        return suggestion;
      }),
    );
  }, [orgMembers]);

  const handleSaveComment = (commentText: string) => {
    if (commentToEdit) {
      // Update existing comment
      updateComment(
        {
          commentId: commentToEdit.comment_id,
          commentText,
        },
        {
          onSuccess: (updatedComment) => {
            onSubmit?.(updatedComment);
          },
        },
      );
    } else {
      // Create new comment
      const request: CreateCommentRequest = {
        entity_type: entityType,
        entity_id: entityId,
        comment_text: commentText,
      };

      createComment(request, {
        onSuccess: (newComment) => {
          onSubmit?.(newComment);
        },
      });
    }
  };

  const loading = isCreating || isUpdating;
  const error = createError || updateError;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <CircularProgress size="1.5rem" />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <Avatar
              src={currentUser?.picture_url}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{ width: 38, height: 38 }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginLeft: theme.spacing(2),
              width: "100%",
              justifyContent: "center",
              cursor: "text",
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: theme.spacing(1) }}>
                {error.message}
              </Alert>
            )}
            <CommentInput
              enableTimestamps={enableTimestamps}
              userSuggestionList={userSuggestionList}
              handleSaveComment={handleSaveComment}
              onCancelClicked={onCancel}
              commentToEdit={commentToEdit}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
